import { Component, ReactNode } from "react";
import { ErrorScreen } from "./ErrorScreen";

/**
 * Whether `error` is the panel's code failing to download: after a deploy the old chunks are gone, and a dropped
 * connection looks the same. Webpack names its error `ChunkLoadError`; a native dynamic import fails differently.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module/.test(error.message)
  );
}

interface State {
  error: unknown;
}

/**
 * Around the lazily loaded panel: when its code cannot be loaded, says so in Polish with a way to reload. Any other
 * error is not the panel's to explain: it goes on to the app's own boundary.
 */
export class PanelErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return (
      <ErrorScreen
        title="Nie udało się wczytać panelu"
        message="Możliwe, że właśnie pojawiła się nowa wersja panelu albo połączenie zostało przerwane. Odśwież stronę, żeby spróbować ponownie."
      />
    );
  }
}
